import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import * as Sentry from '@sentry/react'
import './index.css'
import App from './App.jsx'

// Sentry — only initialize when a DSN is configured (dev / CI stay silent).
// send_default_pii is off so journal text and form values stay client-side.
//
// Integrations:
//   • browserTracingIntegration — routes + web vitals + fetch/xhr spans.
//     Active only when VITE_SENTRY_TRACES_SAMPLE_RATE > 0 (which we keep
//     near-zero in prod for hobby-tier quota sanity).
//   • httpClientIntegration — auto-captures fetch/xhr responses in the
//     server-error range as standalone Sentry events, so a failing API
//     call surfaces even if the caller swallows the exception.
//   • replayIntegration — off by default (privacy + quota). Flip via env
//     if we want session replays for a specific debugging window.
const sentryDsn = import.meta.env.VITE_SENTRY_DSN
if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_SENTRY_RELEASE || undefined,
    tracesSampleRate: Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || 0),
    sendDefaultPii: false,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.httpClientIntegration({
        // Only flag server-range failures automatically — 4xx are usually
        // expected (auth expiry, validation) and would drown the signal.
        failedRequestStatusCodes: [[500, 599]],
      }),
    ],
  })
}

// Minimal, on-brand fallback. Matches the app's vellum + antique-gold
// palette so a render crash degrades to something calm rather than a
// jarring browser error screen.
function ErrorFallback() {
  return (
    <div style={{
      background: '#F2EDE3',
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: "'DM Sans', sans-serif",
      color: '#1C1814',
      padding: 24,
      textAlign: 'center',
    }}>
      <div style={{ maxWidth: 340 }}>
        <p style={{
          fontFamily: "'Playfair Display', Georgia, serif",
          fontStyle: 'italic',
          fontSize: 22,
          color: '#9E8456',
          marginBottom: 12,
          lineHeight: 1.3,
        }}>
          Something didn&rsquo;t load.
        </p>
        <p style={{ fontSize: 13, color: '#55514A', lineHeight: 1.65, fontWeight: 300 }}>
          Refresh the page. If it keeps happening, we&rsquo;ve already been notified.
        </p>
      </div>
    </div>
  )
}

createRoot(document.getElementById('root')).render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
      <App />
    </Sentry.ErrorBoundary>
  </StrictMode>,
)
